import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, Navigate, useSearchParams } from 'react-router-dom';
import { getProjects } from '../data/projects';
import { categories } from '../data/products';
import { ArrowRight, CheckCircle2, ChevronRight, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { JsonLd, useSEO } from '../utils/seo';
export function Project() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const projects = getProjects();
  const project = projects.find(p => p.slug === slug);
  const activeIndex = projects.findIndex(p => p.slug === slug);
  // Every guide starts COLLAPSED. With one guide expanded on arrival, its long
  // detail panel pushed the remaining cards far off screen and the page looked
  // like a single project rather than a browsable index. The customer opens the
  // one they want; tapping it again closes it.
  // ...unless the customer arrived from a direct "open this guide" link (the
  // project pills in the Materials Hub), which passes ?open=1 so the guide they
  // picked is already expanded on arrival.
  const [searchParams] = useSearchParams();
  const openOnArrival = searchParams.get('open') === '1';
  const [isExpanded, setIsExpanded] = useState(openOnArrival);
  // First render lands at the top of the page so the customer sees the guide
  // index and can orient. Every LATER switch scrolls to the detail panel that
  // just expanded — previously the page jumped to the top and the newly
  // selected guide rendered below all seven cards, so tapping a card looked
  // like nothing had happened.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      if (!openOnArrival) {
        window.scrollTo(0, 0);
        return;
      }
      // Arrived with the guide pre-opened — fall through so the page lands on
      // the detail panel rather than the top of the index.
    }
    if (!isExpanded) return;
    const el = document.getElementById('project-detail');
    if (!el) {
      window.scrollTo(0, 0);
      return;
    }
    const navHeight = document.querySelector('nav')?.offsetHeight || 80;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - navHeight - 12,
      behavior: 'smooth'
    });
  }, [slug, isExpanded]);
  const projectDescription = project ? `${project.tagline} See recommended materials, depths, and practical tips for a ${project.name.toLowerCase()} project in the Puget Sound area.` : 'Explore practical gravel, drainage, patio, and landscaping project guides from 206Gravel.';
  useSEO({
    title: project ? `${project.name} Materials & Pro Tips | 206Gravel` : 'Gravel Project Guides | 206Gravel',
    description: projectDescription,
    path: project ? `/projects/${project.slug}` : '/'
  });
  if (!project) {
    return <Navigate to="/" replace data-id="element-3122" data-mp="2eyeopysari9s" />;
  }
  const Icon = project.icon;
  // Helper to find full product details from data/products.ts
  const getProductDetails = (productSlug: string) => {
    for (const cat of categories) {
      const product = cat.products.find(p => p.slug === productSlug);
      if (product) {
        return {
          ...product,
          categoryName: cat.name
        };
      }
    }
    return null;
  };
  return <main className="min-h-screen bg-[#f2f4f0] dark:bg-[#262626] pt-8 pb-24" data-id="element-3123" data-mp="1sr29qx9nu9wg">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: project.name,
      description: projectDescription,
      url: `https://206gravel.com/projects/${project.slug}`,
      supply: project.materials.map(material => {
        const product = getProductDetails(material.slug);
        return {
          '@type': 'HowToSupply',
          name: product ? product.name : material.role,
          ...(product ? {
            url: `https://206gravel.com/shop/${product.slug}`,
            image: product.image
          } : {})
        };
      }),
      step: project.tips.map((tip, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: `Step ${index + 1}`,
        text: tip,
        url: `https://206gravel.com/projects/${project.slug}#step-${index + 1}`
      }))
    }, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: project.faqs.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a
        }
      }))
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Shop by Project',
        item: 'https://206gravel.com/projects/new-gravel-driveway'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: project.name,
        item: `https://206gravel.com/projects/${project.slug}`
      }]
    }]} data-id="element-3124" data-mp="2qeb1vb462h7f" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3125" data-mp="9c8qkfhb3hgq">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm text-stone-500 dark:text-stone-400 mb-6" aria-label="Breadcrumb" data-id="element-3126" data-mp="4at6oqqv7gzx">
          <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-3127" data-mp="i8ho1olq4608">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2 shrink-0" data-id="element-3128" data-mp="3mp230cerwfb6" />
          <Link to="/projects/new-gravel-driveway" className="hover:text-brand-orange transition-colors whitespace-nowrap" data-id="element-3129" data-mp="3mpk9e1v5g9wt">
            Shop by Project
          </Link>
          <ChevronRight className="w-4 h-4 mx-2 shrink-0" data-id="element-3130" data-mp="3vhm73sdu3c3e" />
          <span className="text-stone-900 dark:text-white font-medium truncate" data-id="element-3131" data-mp="3vj6w72hdq2tv">
            {project.name}
          </span>
        </nav>

        {/* Project switcher — mirrors the Materials Hub: an accent rule, a
            large section heading, and a grid of photo-backed cards. The old
            icon-tile tabs read as navigation chrome; these read as browsable
            guides, which is what a customer who doesn't know their material is
            actually looking for. The photo comes from each guide's primary
            material, so the card shows the real thing being delivered. */}
        <section className="mb-14" aria-label="Project guides" data-id="element-3132" data-mp="1gnq7vfmdxdh3">
          <motion.div initial={{
          width: 0,
          opacity: 0
        }} whileInView={{
          width: 64,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.65,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-6 h-1 rounded-full bg-brand-orange" data-id="element-3133" data-mp="1gn6mfoxj06yb" />
          <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-baseline md:gap-6" data-id="element-3134" data-mp="xx8k7pget82l">
            <h2 className="font-heading text-4xl font-extrabold leading-[1.1] tracking-tight text-brand-charcoal dark:text-white md:text-5xl" data-id="element-3135" data-mp="xuvu2p0hzreg">
              Project <span className="font-black text-[#04A05C]" data-id="element-3136" data-mp="29b2jwvux62w5">Guides</span>
            </h2>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#17A348]" data-id="element-3137" data-mp="3tupmorq62gn5">
              — Browse by project
            </span>
          </div>
          <p className="mb-8 max-w-2xl font-sub text-lg font-medium leading-relaxed tracking-wide text-brand-charcoal dark:text-white" data-id="element-3138" data-mp="3oxpfgdhkz7ug">
            Pick the job you're doing and we'll show the exact materials, depths,
            and quantities that job needs.
          </p>

          <div className="grid grid-cols-1 gap-[22px] sm:grid-cols-2 sm:gap-[26px] md:grid-cols-3" data-id="element-3139" data-mp="3a7rwcbyid8c4">
            {projects.map((p, index) => {
            const isActive = p.slug === project.slug;
            const heroMaterial = getProductDetails(p.materials[0]?.slug ?? '');
            return <motion.div key={p.slug} style={{
              order: index * 2
            }} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              delay: Math.min(index, 5) * 0.035
            }} data-id="element-3140" data-mp="2w88o7j5p5wzt" data-mpi={index}>
                  <Link to={`/projects/${p.slug}`} aria-current={isActive ? 'page' : undefined} aria-expanded={isActive ? isExpanded : false} onClick={() => setIsExpanded(prev => isActive ? !prev : true)} className={`group relative block h-[13.2rem] overflow-hidden rounded-lg border bg-stone-200 shadow-sm transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] focus-visible:ring-offset-2 hover:shadow-md sm:h-48 dark:bg-white/10 dark:focus-visible:ring-offset-[#262626] ${isActive && isExpanded ? 'border-[#04A05C] ring-2 ring-[#04A05C]' : 'border-stone-200 dark:border-white/10'}`} data-id="element-3141" data-mp="88s2lay5lqhg">
                    {heroMaterial && <img src={heroMaterial.images?.[0] || heroMaterial.image} alt="" loading={index < 3 ? 'eager' : 'lazy'} decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" data-id="element-3142" data-mp="3bqtf3xekfpdy" />}
                    {isActive && isExpanded && <span className="absolute left-0 top-4 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wide text-[#fffcf9] shadow-sm bg-[#006239]" data-id="element-3143" data-mp="2ufo96xjmg8ms">
                        Open — tap to close
                      </span>}
                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-[#FBFCFB] px-3 py-2.5 text-stone-650 sm:px-4 dark:bg-black/65 dark:text-white dark:backdrop-blur-[1px]" data-id="element-3144" data-mp="347u1679viq1z">
                      <div className="min-w-0 font-sub" data-id="element-3145" data-mp="3d54cttqlsh7f">
                        <p className="truncate text-[9px] uppercase tracking-[0.12em] text-stone-900 dark:text-white/80 font-[400] font-[Cabin,_sans-serif]" data-id="element-3146" data-mp="3963x4k3f9uon">
                          Project guide
                        </p>
                        <h3 className="mt-0.5 truncate font-[Cabin,_sans-serif] text-[18px] font-[500] sm:text-base" data-id="element-3147" data-mp="3i6jtzapnu3kj">
                          {p.name}
                        </h3>
                        <p className="mt-0.5 truncate text-[11px] leading-snug text-stone-600 dark:text-white/70 font-[Cabin,_sans-serif]" data-id="element-3148" data-mp="1nt0zk9qxf13g">
                          {p.materials.length} {p.materials.length === 1 ? 'material' : 'materials'} · {p.typicalYards}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>;
          })}

            {/* The selected guide expands INSIDE the grid, as a full-width row
                ordered to fall immediately after its own card (cards take even
                orders, this panel takes the odd slot right after the active
                one). On mobile that means the content opens directly under the
                card the customer just tapped instead of below all seven. */}
            <div id="project-detail" hidden={!isExpanded} className="col-span-full scroll-mt-24" style={{
            order: activeIndex * 2 + 1
          }} data-id="element-3149" data-mp="7tc75czz19tc">
        {/* Header */}
        <div className="mb-12" data-id="element-3150" data-mp="zl1kwxyb79qu">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-orange/10 text-brand-orange mb-6" data-id="element-3151" data-mp="zk09uthamn94">
            <Icon className="w-8 h-8" data-id="element-3152" data-mp="2iao0ndtjilgt" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-stone-900 dark:text-white mb-4" data-id="element-3153" data-mp="2n87c65yusk52">
            {project.name}
          </h1>
          <p className="text-xl md:text-2xl text-stone-600 dark:text-stone-300 font-medium mb-6" data-id="element-3154" data-mp="2w82sep79n5by">
            {project.tagline}
          </p>
          <div className="prose prose-lg dark:prose-invert text-stone-600 dark:text-stone-400 max-w-none" data-id="element-3155" data-mp="3164cndgfhap0">
            <p data-id="element-3156" data-mp="364z3n2oju5k7">{project.intro}</p>
          </div>
          <div className="mt-6 inline-flex items-center px-4 py-2 rounded-full bg-stone-100 dark:bg-white/5 text-stone-700 dark:text-stone-300 text-sm font-medium border border-stone-200 dark:border-white/10" data-id="element-3157" data-mp="3awyxqehxu48v">
            <span className="mr-2" data-id="element-3158" data-mp="3jxx5n7vhyvwq">Typical project size:</span>
            <span className="font-bold text-stone-900 dark:text-white" data-id="element-3159" data-mp="3jzhvwlas3sos">
              {project.typicalYards}
            </span>
          </div>
        </div>

        {/* Materials Section */}
        <div className="mb-16" data-id="element-3160" data-mp="1ous24343qd0f">
          <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6" data-id="element-3161" data-mp="1jvfhzq4m7k1j">
            Recommended Materials
          </h2>
          {/* Materials read as an ordered SPEC LIST, not cards. Rounded,
              shadowed, photo-led cards looked identical to the project tiles
              above, so the page appeared to be showing more projects. Square
              edges, shared rules between rows, a step number, and a small
              square thumbnail make these read as the layers of one job. */}
          <div className="border-t border-stone-300 dark:border-white/15" data-id="element-3162" data-mp="2hjfroavvvojs">
            {project.materials.map((mat, idx) => {
                  const product = getProductDetails(mat.slug);
                  if (!product) return null;
                  return <motion.div key={mat.slug} initial={{
                    opacity: 0,
                    y: 12
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: idx * 0.06
                  }} className="flex gap-4 border-b border-stone-300 py-6 sm:gap-6 dark:border-white/15" data-id="element-3163" data-mp="n5ol5k8upksk" data-mpi={idx}>
                  <div className="flex shrink-0 flex-col items-center gap-3" data-id="element-3164" data-mp="2yucn1pi6gc2x">
                    <span className="flex h-7 w-7 items-center justify-center bg-stone-900 text-xs font-bold text-white dark:bg-white dark:text-stone-900" data-id="element-3165" data-mp="2k3c60w3d79v6">
                      {idx + 1}
                    </span>
                    <img src={product.image} alt={product.name} loading="lazy" decoding="async" className="h-20 w-20 object-cover sm:h-24 sm:w-24" data-id="element-3166" data-mp="2b5a8wrmr51d6" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-3167" data-mp="216axx2eiktn0">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-3168" data-mp="263kyne7csrq5">
                      <h3 className="font-heading text-xl font-bold text-stone-900 dark:text-white" data-id="element-3169" data-mp="3vi97j5gqjrjf">
                        {product.name}
                      </h3>
                      <div className="font-heading text-lg font-bold tabular-nums text-stone-900 dark:text-white" data-id="element-3170" data-mp="4ade9qcvvjxe">
                        ${product.pricePerYard}
                        <span className="text-sm font-normal text-stone-500 dark:text-stone-400" data-id="element-3171" data-mp="1wfc38a66u6sb">
                          /yd
                        </span>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] font-bold uppercase tracking-wider" data-id="element-3172" data-mp="1mfbf8axdj2hs">
                      <span className="text-brand-orange" data-id="element-3173" data-mp="1cq1oozi6hrvs">{mat.role}</span>
                      <span className="text-stone-300 dark:text-stone-600" data-id="element-3174" data-mp="1coqiyu56aa21">·</span>
                      <span className="text-stone-500 dark:text-stone-400" data-id="element-3175" data-mp="18oy98e41lxv3">
                        Depth: {mat.depth}
                      </span>
                    </div>
                    <p className="mt-2.5 text-stone-600 dark:text-stone-300" data-id="element-3176" data-mp="yqiiic8xgqm4">
                      <span className="font-semibold text-stone-900 dark:text-white" data-id="element-3177" data-mp="trfpxra4wcv1">
                        Why:{' '}
                      </span>
                      {mat.why}
                    </p>
                    <Link to={`/shop/${product.slug}`} className="mt-3 inline-flex items-center text-sm font-bold text-brand-orange transition-colors hover:text-brand-orange/80" data-id="element-3178" data-mp="olsnkqbxymre">
                      View product details
                      <ArrowRight className="w-4 h-4 ml-1" data-id="element-3179" data-mp="yjgknmx0s6cq" />
                    </Link>
                  </div>
                </motion.div>;
                })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-3180" data-mp="5vaziz220fcd">
          {/* Pro Tips */}
          <div data-id="element-3181" data-mp="auxbz6a25rk4">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3182" data-mp="audqjfl78l1c">
              <CheckCircle2 className="w-6 h-6 mr-2 text-[#04A05C]" data-id="element-3183" data-mp="fmp3pk4ya3bu" />
              Pro Tips
            </h2>
            <ul className="space-y-4" data-id="element-3184" data-mp="3eaumxhg1n3ji">
              {project.tips.map((tip, idx) => <li key={idx} id={`step-${idx + 1}`} className="flex items-start scroll-mt-24" data-id="element-3185" data-mp="3ja77fvjfzyix" data-mpi={idx}>
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-orange mt-2 mr-3" data-id="element-3186" data-mp="3b9j3hqjceolr" />
                  <span className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3187" data-mp="36bhmrc9e31dg">
                    {tip}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* FAQs */}
          <div data-id="element-3188" data-mp="3g1sqaxp5v8jt">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3189" data-mp="3g22k6wdbtzus">
              <HelpCircle className="w-6 h-6 mr-2 text-brand-orange" data-id="element-3190" data-mp="2sbf6tvkn6i0u" />
              Project FAQs
            </h2>
            <div className="space-y-6" data-id="element-3191" data-mp="2negenikjgl5a">
              {project.faqs.map((faq, idx) => <div key={idx} data-id="element-3192" data-mp="32axno3599noo" data-mpi={idx}>
                  <h4 className="font-bold text-stone-900 dark:text-white mb-2" data-id="element-3193" data-mp="32706hn28n67f">
                    {faq.q}
                  </h4>
                  <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed" data-id="element-3194" data-mp="hiceebrwxvca">
                    {faq.a}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl p-8 md:p-12 text-center bg-[#1a1a1a]" style={{
              backgroundColor: "#575656"
            }} data-id="element-3195" data-mp="k1yyuyb8apcp">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4" data-id="element-3196" data-mp="p0k09127vf2q">
            Ready to start your project?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto" data-id="element-3197" data-mp="f3xi4k19z24e">
            Get an instant estimate including delivery for all the materials you
            need.
          </p>
          <Link to={`/?from=project&project=${project.slug}#estimator`} className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-brand-orange hover:bg-brand-orange/90 rounded-xl transition-colors" data-id="element-3198" data-mp="17t4na1v8ipym">
            Get an Estimate
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-3199" data-mp="y1i5tnsazunf" />
          </Link>
        </div>
            </div>
          </div>
        </section>
      </div>
    </main>;
}